/**
 * Product Browser Component
 * Reusable component for browsing product catalogs within tab content.
 * Handles category navigation, product grid, search, and selection.
 */

import './ProductBrowser.css'
import type { Product, ProductCategory, Layer } from '@/types/garden'
import { api } from '@/api/client'
import { getSelectedProduct, setSelectedProduct } from './state'
import { debug } from '@/utils/debug'
import { t } from '@/i18n'

export interface ProductBrowserConfig {
  /** Layer to browse (determines which categories are shown) */
  layer: Layer
  /** Accent color for selections (CSS color string) */
  accentColor: string
  /** Show search input (default true) */
  showSearch?: boolean
  /** Placeholder text for search input */
  searchPlaceholder?: string
  /** Called when a product is selected */
  onProductSelected?: (product: Product | null) => void
}

export class ProductBrowser {
  private container: HTMLElement | null = null
  private config: ProductBrowserConfig
  private categories: ProductCategory[] = []
  private subcategories: ProductCategory[] = []
  private products: Product[] = []
  private activeCategory: ProductCategory | null = null
  private activeSubcategory: ProductCategory | null = null
  private searchQuery: string = ''
  private isLoading: boolean = false
  private searchTimeout: ReturnType<typeof setTimeout> | null = null
  private categoriesLoaded: boolean = false

  constructor(config: ProductBrowserConfig) {
    this.config = {
      showSearch: true,
      searchPlaceholder: t('product.searchPlaceholder', 'Search products...'),
      ...config,
    }
  }

  /**
   * Render the browser into a container element
   */
  render(container: HTMLElement): void {
    this.container = container
    this.renderContent()

    // Load categories eagerly
    if (!this.categoriesLoaded) {
      this.loadCategories()
    }
  }

  /**
   * Clean up
   */
  destroy(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    this.container = null
  }

  /**
   * Force refresh of the product list
   */
  refresh(): void {
    if (this.activeCategory) {
      this.loadProducts()
    }
  }

  // ============================================================================
  // Data loading
  // ============================================================================

  private async loadCategories(): Promise<void> {
    try {
      this.categories = await api.getCategories(this.config.layer)
      this.categoriesLoaded = true
      debug.log('ProductBrowser', `Loaded ${this.categories.length} categories for ${this.config.layer}`)
      this.renderContent()
    } catch (error) {
      debug.error('ProductBrowser', 'Failed to load categories', error)
    }
  }

  private async loadSubcategories(parentId: string): Promise<void> {
    try {
      this.subcategories = await api.getSubcategories(parentId)
      debug.log('ProductBrowser', `Loaded ${this.subcategories.length} subcategories for ${parentId}`)
      this.renderContent()
    } catch (error) {
      debug.error('ProductBrowser', 'Failed to load subcategories', error)
      this.subcategories = []
    }
  }

  private async loadProducts(): Promise<void> {
    this.isLoading = true
    this.renderContent()

    try {
      const categoryId = this.activeSubcategory?.id ?? this.activeCategory?.id
      const result = await api.searchProducts({
        categoryId: categoryId ?? undefined,
        layer: this.config.layer,
        search: this.searchQuery || undefined,
      })

      this.products = result.products
      debug.log('ProductBrowser', `Loaded ${this.products.length} products`)
    } catch (error) {
      debug.error('ProductBrowser', 'Failed to load products', error)
      this.products = []
    }

    this.isLoading = false
    this.renderContent()

    // Restore focus to search input after re-render
    if (this.searchQuery) {
      this.focusSearchInput()
    }
  }

  // ============================================================================
  // Rendering
  // ============================================================================

  private renderContent(): void {
    if (!this.container) return

    const selectedProduct = getSelectedProduct()
    const showSearch = this.config.showSearch !== false

    this.container.innerHTML = `
      <div class="product-browser" style="--layer-accent: ${this.config.accentColor}">
        ${showSearch ? this.renderSearch() : ''}
        ${this.renderCategoryBar()}
        ${this.subcategories.length > 0 ? this.renderSubcategoryBar() : ''}
        ${this.renderProductArea(selectedProduct)}
      </div>
    `

    this.wireEvents()
  }

  private renderSearch(): string {
    const hasClearBtn = this.searchQuery.length > 0
    return `
      <div class="product-search">
        <div class="product-search-wrapper">
          <i class="fa-solid fa-magnifying-glass product-search-icon"></i>
          <input
            type="text"
            class="product-search-input"
            placeholder="${this.config.searchPlaceholder}"
            value="${this.escapeHtml(this.searchQuery)}"
          />
          <button class="product-search-clear" ${hasClearBtn ? '' : 'style="display:none"'}>
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>
    `
  }

  private renderCategoryBar(): string {
    if (this.categories.length === 0) return ''

    return `
      <div class="product-category-bar">
        ${this.categories.map(cat => `
          <span class="product-category-chip${this.activeCategory?.id === cat.id ? ' active' : ''}" data-category-id="${cat.id}">
            ${this.escapeHtml(cat.name)}
          </span>
        `).join('')}
      </div>
    `
  }

  private renderSubcategoryBar(): string {
    return `
      <div class="product-subcategory-bar">
        ${this.subcategories.map(sub => `
          <span class="product-subcategory-chip${this.activeSubcategory?.id === sub.id ? ' active' : ''}" data-subcategory-id="${sub.id}">
            ${this.escapeHtml(sub.name)}
          </span>
        `).join('')}
      </div>
    `
  }

  private renderProductArea(selectedProduct: Product | null): string {
    if (this.isLoading) {
      return `
        <div class="product-loading">
          <i class="fa-solid fa-spinner"></i>
          ${t('product.loading', 'Loading products...')}
        </div>
      `
    }

    if (!this.activeCategory && !this.searchQuery) {
      return `
        <div class="product-empty">
          <i class="fa-solid fa-folder-open"></i>
          ${t('product.selectCategory', 'Select a category to browse products')}
        </div>
      `
    }

    if (this.products.length === 0) {
      // If subcategories exist but none selected, hint to pick one
      if (this.subcategories.length > 0 && !this.activeSubcategory && !this.searchQuery) {
        return `
          <div class="product-empty">
            <i class="fa-solid fa-folder-open"></i>
            ${t('product.selectSubcategory', 'Select a subcategory to browse products')}
          </div>
        `
      }
      return `
        <div class="product-empty">
          <i class="fa-solid fa-inbox"></i>
          ${t('product.noResults', 'No products found')}
        </div>
      `
    }

    return `
      <div class="product-grid">
        ${this.products.map(p => {
          const isSelected = selectedProduct?.id === p.id
          const iconHtml = p.iconUrl
            ? `<img src="${this.escapeHtml(p.iconUrl)}" alt="" loading="lazy" />`
            : `<i class="fa-solid fa-cube"></i>`
          const infoHtml = this.config.layer === 'plants' && p.code
            ? `<button class="product-card-info" data-info-code="${this.escapeHtml(p.code)}" data-info-product-id="${this.escapeHtml(p.id)}" data-info-name="${this.escapeHtml(p.name)}" title="${this.escapeHtml(t('plantInfo.infoButton'))}"><i class="fa-solid fa-circle-info"></i></button>`
            : ''
          return `
            <div class="product-card${isSelected ? ' selected' : ''}" data-product-id="${p.id}" title="${this.escapeHtml(p.name)}">
              ${infoHtml}
              <div class="product-card-icon">${iconHtml}</div>
              <span class="product-card-name">${this.escapeHtml(p.name)}</span>
            </div>
          `
        }).join('')}
      </div>
    `
  }

  // ============================================================================
  // Event wiring
  // ============================================================================

  private wireEvents(): void {
    if (!this.container) return

    // Search input
    const searchInput = this.container.querySelector('.product-search-input') as HTMLInputElement
    const clearBtn = this.container.querySelector('.product-search-clear') as HTMLElement
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        this.searchQuery = searchInput.value.trim()
        // Show/hide clear button
        if (clearBtn) {
          clearBtn.style.display = this.searchQuery.length > 0 ? '' : 'none'
        }
        this.debouncedSearch()
      })
    }
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        this.searchQuery = ''
        if (searchInput) {
          searchInput.value = ''
          searchInput.focus()
        }
        clearBtn.style.display = 'none'
        this.loadProducts()
      })
    }

    // Category chips
    this.container.querySelectorAll('.product-category-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const catId = (chip as HTMLElement).dataset.categoryId
        const cat = this.categories.find(c => c.id === catId)
        if (cat) {
          this.activeCategory = cat
          this.activeSubcategory = null
          this.subcategories = []
          this.loadSubcategories(cat.id)
          this.loadProducts()
        }
      })
    })

    // Subcategory chips
    this.container.querySelectorAll('.product-subcategory-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const subId = (chip as HTMLElement).dataset.subcategoryId
        this.activeSubcategory = this.subcategories.find(s => s.id === subId) ?? null
        this.loadProducts()
      })
    })

    // Plant info buttons on product cards
    this.container.querySelectorAll('.product-card-info').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        const el = btn as HTMLElement
        const code = el.dataset.infoCode
        const productId = el.dataset.infoProductId
        const name = el.dataset.infoName
        if (code) {
          import('./PlantInfoModal').then(m => m.plantInfoModal.open({
            productCode: code,
            productId,
            productName: name,
          }))
        }
      })
    })

    // Product cards
    this.container.querySelectorAll('.product-card').forEach(card => {
      card.addEventListener('click', () => {
        const productId = (card as HTMLElement).dataset.productId
        const product = this.products.find(p => p.id === productId)
        if (!product) return

        const current = getSelectedProduct()
        if (current?.id === product.id) {
          // Deselect
          setSelectedProduct(null)
          this.config.onProductSelected?.(null)
        } else {
          setSelectedProduct(product, this.config.layer)
          this.config.onProductSelected?.(product)
        }

        this.renderContent()
      })
    })
  }

  private debouncedSearch(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    // Only search with 3+ chars
    if (this.searchQuery.length > 0 && this.searchQuery.length < 3) return
    // When search cleared with no category, just re-render (no API call)
    if (this.searchQuery.length === 0 && !this.activeCategory) {
      this.products = []
      this.renderContent()
      this.focusSearchInput()
      return
    }
    this.searchTimeout = setTimeout(() => {
      this.loadProducts()
    }, 300)
  }

  private focusSearchInput(): void {
    const input = this.container?.querySelector('.product-search-input') as HTMLInputElement
    if (input) {
      input.focus()
      input.setSelectionRange(input.value.length, input.value.length)
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
